// Coaster page, two styles: the two-sided coaster (a word per seat) and the name / logo coaster (a text on a plain face).
// The DOCUMENT (src/doc.js) is the state: every control builds a NEW doc through sanitize(), and the worker
// (geometry.worker.js via worker-client.js) builds the meshes 120 ms after the last change. No geometry runs here. Same
// shape as apps/coffee-cup/src/main.js. The two words, and the logo text, go to the worker and into the share fragment
// (the address bar) and nowhere else: never a beacon, a file name, a 3MF title, storage or a log. An uploaded logo's file
// text goes page -> worker -> readSvg and nowhere else; the page keeps only the numbers the worker made of it (`art`).
import '@3dp/ui/shell.css';
import '@3dp/ui/picker.css';   // the font control and its sheet (unify-ui ship 4a): before the tool's own stylesheet
import '@3dp/ui/colours.css';  // the colour control (unify-ui ship 4b): before the tool's own stylesheet too
import './main.css';
import { publishBarHeight, publishHeaderHeight } from '@3dp/ui/bar-height';
import { pageLanguage } from '@3dp/ui/locales';
import { asset } from '@3dp/ui/asset';
import { S } from './strings.js';
import { DEFAULT_DOC, LIMITS, LOOKS, SHAPES, EDGES, MODES, STYLES, FINISHES, SOURCES, sanitize, lookKey, sidesKey } from './doc.js';
import { GEOM } from './relief.js';   // plain numbers, no engine code: the copy quotes them
import { createWorkerClient } from './worker-client.js';
import { createPreview } from './preview3d.js';
import { COLOUR_CORE, slotName } from '@3dp/core/filaments';
import { createColours } from '@3dp/ui/colours';
import { recordOf, asListed, askedFor, offerFor, megabytes, listMayHelp, builtFontId } from '@3dp/core/fonts/choose';
import { curatedEntry, fontKey } from '@3dp/core/fonts/catalog';
import { PICKER_CORE } from '@3dp/core/fonts/picker-core';
import { createFontControl } from '@3dp/ui/font-control';
import { hit } from '@3dp/core/stats';
import { isLineBrowser, externalBrowserUrl, shareUrl, readShare } from './share.js';
import { wireLangSwitch } from '@3dp/ui/switch';   // ship 3 (R21): the switch's links carry the design
import { mountLangOffer } from '@3dp/ui/offer';

publishBarHeight(document.querySelector('.ui-bar'));   // --ui-bar-h: shell.css ends the desktop panel above the fixed bar
publishHeaderHeight(document.querySelector('header.ui-header')); // --ui-header-h: ship 3's taller header row; the desktop panel leaves its room too

const $ = (s) => document.querySelector(s);
const START_TIMEOUT_MS = 60000; // a worker that has not said `ready` by then counts as failed to load (worker-client.js)
const ART_TIMEOUT_MS = 10000;        // reading a file: this long after the worker started on it, the worker is ended — the engine cannot be interrupted
const ART_BUILD_TIMEOUT_MS = 8000;   // the same for a build that reads uploaded art (logo spec D10)
const ART_EXPORT_TIMEOUT_MS = 20000; // and for an export that reads it
const BUILD_TIMEOUT_MS = 10000;      // a build WITHOUT a file: this long after the worker started on it the worker is ended and a new one started (unify-ui ruling R22)
const SVG_MAX_BYTES = 1048576;       // the reader's limit in characters; a file has at least as many bytes, so a bigger one is refused unread
const THUMB = [96, 64];              // #thumb's CSS size in px (main.css)
const SKIPPED_KEYS = ['stroke', 'text', 'image', 'hidden', 'bad', 'background', 'clipped', 'stroked'];

// A worker that cannot work is permanent for this page: once true, renderIssues() always shows S.fatal.
const startLimit = () => { const v = window.__COASTER__ && window.__COASTER__.startTimeoutMs; return v > 0 ? v : START_TIMEOUT_MS; };
let fatal = false;
const worker = createWorkerClient({ url: window.__WORKER_URL__, recycleAfter: 300, startLimitMs: startLimit, onFatal: () => { fatal = true; render(); } });

// ---- state: doc (from a share link or the default), the notice above the form, the last build ----
const shareResult = readShare(location.hash);
let doc, notice = null;

// ship 3 (R21): the language switch and the English-page offer. An uploaded logo's file never
// travels in a link (forShare keeps the mode) — the note says so while the menu is open.
wireLangSwitch(window, { linkFor: (href) => shareUrl(href, doc), loses: () => doc.logo.src === 'svg' });
mountLangOffer(window, { linkFor: (href) => shareUrl(href, doc) });   // the offer's link carries the design too (B1)
// A page opened with no link starts on its language's words (ruling R69: the Thai table's are the document's defaults, so
// the Thai page and an unchanged design's link are what they were); a link carries its own words and opens as it is.
const START = sanitize({ ...DEFAULT_DOC, ...S.start });
if (shareResult === null) doc = START;
else if (shareResult.error) { notice = shareResult.error.code === 'newer-version' ? S.newerLink : S.badLink; doc = START; }
else { doc = shareResult.doc; if (doc.style === 'logo' && doc.logo.src === 'svg') notice = S.linkNeedsLogo; }   // a link never carries the file
let builtOnce = false;           // the first successful build starts the swing
// `art` is the outline the worker made of an uploaded file ({ points, rings, w, h, pieces }): numbers only, already
// reduced to at most 5000 points. It lives in this variable and nowhere else — not in the doc, the link, storage or
// the DOM — so a reload forgets it.
let art = null, builtArt = null, reading = false, uploadError = null, artSkipped = null, shownSkipped = null;
let builtDoc = null, buildFailed = false, buildTimedOut = false, problems = [], meta = null, preview = null, previewFailed = false;
let fontList = null;            // the list's entries (fonts/curated.json: 17 bundled, 12 downloaded), once it has loaded
let lastFont = null;            // the font of the last build that had its font: where a download that fails falls back to

// Window for the browser tests and the hub's ready signal (`ready`: window.__COASTER__?.built === true).
window.__COASTER__ = {
  built: false, builds: 0, picks: 0, lastMs: null, problems: [], startTimeoutMs: startLimit(),
  artTimeoutMs: ART_TIMEOUT_MS, artBuildTimeoutMs: ART_BUILD_TIMEOUT_MS, artExportTimeoutMs: ART_EXPORT_TIMEOUT_MS,    // the limits a test may shorten (read when a request is sent)
  buildTimeoutMs: BUILD_TIMEOUT_MS,
  // A browser test's knob for the text budget (src/api.js limitFor): a number LOWERS the worker's limit for the requests
  // sent from then on — it cannot raise it; null (always, outside a test) is the worker's own 56 000. `budgetInForce` is
  // what the worker said it used for the build on show (null: its own) — the test reads the knob back there.
  textBudget: null, budgetInForce: null,
  fontProgress: null,                                        // the last progress of a font download: { id, received, total } (read-only, for the tests)
  doc: () => doc, meta: () => meta, art: () => art,
  settled: () => builtDoc === doc && builtArt === art,      // the scene on screen was built from the current doc and art
  sceneCount: () => (preview ? preview.count() : 0),
  view: () => (preview ? preview.view() : null), swinging: () => (preview ? preview.swinging() : false),
  angles: () => (preview ? preview.angles() : null),
  setView: (name) => { if (preview) preview.setView(name); },
  sceneRect: () => (preview ? preview.sceneRect() : null),
  partRect: (name) => (preview ? preview.rectOf(name) : null),
  canvasRect: () => { const r = $('#preview').getBoundingClientRect(); return { left: r.left, top: r.top, width: r.width, height: r.height }; },
};

// checkVisibility() is missing before Safari 17.4 / iOS 17.4; offsetParent is the fallback (as in tray's preview3d.js).
const shown = (el) => (typeof el.checkVisibility === 'function' ? el.checkVisibility() : el.offsetParent !== null);
// The view pills: the one that matches the camera is lit; a drag (or a swing under way) lights none (view() is null then).
let lit;
function lightPill(name) {
  if (name === lit) return;
  lit = name;
  for (const b of $('#view').children) { b.classList.toggle('on', b.dataset.k === name); b.setAttribute('aria-pressed', String(b.dataset.k === name)); }
}
hit('coaster', 'open');
hit('coaster', 'choice', `lang:${pageLanguage().id}`); // R25: the page's OWN address's language, never the browser's
try { preview = createPreview($('#preview'), { onView: lightPill }); } catch { previewFailed = true; }
if (preview) lightPill(preview.view());
if (preview && doc.style === 'logo') preview.setView('front');   // a logo link never swings: setView marks the view as chosen

// ---- static copy: every user-visible string comes from strings.js ----
const text = (sel, v) => { $(sel).textContent = v; };
text('#title', S.titles[doc.style]);
text('#l-g-shape', S.gShape); text('#l-g-colour', S.gColour);
for (const b of $('#style').children) b.textContent = S.styles[b.dataset.k];
$('#style').setAttribute('aria-label', S.styleGroup);
for (const b of $('#src').children) b.textContent = S.sources[b.dataset.k];
text('#pick', S.pick); $('#thumb').setAttribute('aria-label', S.thumb);
text('#l-text', S.text); text('#text-hint', S.textHint(LIMITS.lines, LIMITS.lineChars)); $('#text').setAttribute('aria-label', S.text);
text('#l-finish', S.finish); text('#l-scale', S.scale);
for (const [k, v] of Object.entries(S.finishes)) text(`#finish button[data-k="${k}"]`, v);
$('#scale').min = String(LIMITS.scale[0]); $('#scale').max = String(LIMITS.scale[1]);
text('#l-a', S.a); text('#l-b', S.b); text('#l-font', S.font); text('#word-hint', S.wordHint);
text('#l-shape', S.shape); text('#l-edge', S.edge); text('#l-mode', S.mode);
for (const [k, v] of Object.entries(S.shapes)) text(`#shape button[data-k="${k}"]`, v);
for (const [k, v] of Object.entries(S.edges)) text(`#edge button[data-k="${k}"]`, v);
for (const [k, v] of Object.entries(S.modes)) text(`#mode button[data-k="${k}"]`, v);
for (const [k, v] of Object.entries(S.views)) text(`#view button[data-k="${k}"]`, v);
$('#view').setAttribute('aria-label', S.viewGroup);
$('#view').addEventListener('click', (e) => {
  const b = e.target.closest('button[data-k]');
  if (b && preview && Object.hasOwn(S.views, b.dataset.k)) preview.setView(b.dataset.k);
});
text('#dl3mf', S.dl3mf); text('#dlstl', S.dlstl); text('#share', S.share);
$('#a').setAttribute('aria-label', S.a); $('#b').setAttribute('aria-label', S.b);
// The notice stays: it is set once, here, and build() never touches it.
if (previewFailed) { text('#nogl', S.noGl); $('#nogl').classList.remove('hidden'); $('#preview').classList.add('hidden'); }

// ---- the colours (unify-ui ship 4b: @3dp/ui/colours, D3 — name-tag's control): the body's and the words' (the logo's) rows,
// each a swatch with the colour's name under it, every filament and brand colour plus the visitor's own in one tap; the
// colour themes (this tool's own, D3) as strips. An own colour is stored and shown; for what prints it counts as its
// nearest filament (api.js warnings, the legend's and the table's names: R99) — swap mode's colour changes carry its hex. ----
const colours = createColours({ root: $('#colours'), S, core: COLOUR_CORE, looksLabel: S.looks,
  onChange: (key, hex) => change({ ...doc, colors: { ...doc.colors, [key]: hex } }),
  onLook: (key) => { if (Object.hasOwn(LOOKS, key)) change({ ...doc, colors: { ...LOOKS[key] } }); } });
const LOOK_LIST = Object.entries(LOOKS).map(([key, c]) => ({ key, name: S.lookNames[key], colours: { ...c } }));

// ---- render: doc → every control, readout, legend, issues, buttons ----
function renderReadout() {
  const logo = doc.style === 'logo';
  text('#r-size', meta ? S.size(doc.shape, meta.size.d, meta.size.h) : '');
  // the logo style holds water only when the build says there is some (a flat edge holds none)
  text('#r-hold', meta && (!logo || meta.holdMl >= 1) ? S.hold(meta.holdMl) : '');
  // a count shows only for the document that was built, and only when there is something to count (the two-sided style's)
  for (const w of ['a', 'b']) text('#rows-' + w, !logo && meta && meta.rows && builtDoc === doc && doc[w] && meta.rows[w] > 0 ? S.rows(meta.rows[w]) : '');
}
function renderLegend() {
  const ul = $('#legend'), names = doc.style === 'logo' ? S.logoPartNames : S.partNames;
  ul.replaceChildren();
  for (const e of meta ? meta.extruders : []) {
    const li = document.createElement('li'), sw = document.createElement('span');
    sw.className = 'sw'; sw.style.background = e.hex;
    li.append(sw, `${S.slot(e.n)} ${slotName(e.hex)} · ${e.parts.map((p) => names[p]).join(' + ')}`);   // the filament to load (an own colour's nearest: R99)
    ul.append(li);
  }
}
// the swap note quotes the heights the table's layers start at, from the build's own table (never typed)
function modeNoteText() {
  if (!meta) return '';
  const zt = meta.zTable;
  if (doc.style === 'logo' && meta.extruders.length === 1 && !zt.length) return S.modeNote.oneColour;   // engraved, or nothing on the face
  if (doc.mode !== 'swap') return S.modeNote.multi;
  if (zt.length === 2) return S.modeNote.swap(zt[0].z, zt[1].z);
  if (zt.length === 1) return S.modeNote.swapOne(GEOM.layer, zt[0].z) + (doc.edge === 'lip' ? ' ' + S.modeNote.lipToo : '');
  return '';
}
function renderZTable() {
  const ol = $('#ztable'), names = doc.style === 'logo' ? S.logoZNames : S.zNames;
  ol.replaceChildren();
  if (!meta || doc.mode !== 'swap') return;
  for (const r of meta.zTable) { const li = document.createElement('li'); li.textContent = S.zLine(r.layer, slotName(r.hex), names[r.to]); ol.append(li); }   // the filament to change to (an own colour's nearest)
}
const rank = { error: 0, warn: 1, info: 2 };
function problemText(p) {
  const m = Object.hasOwn(S.problem, p.code) ? S.problem[p.code] : p.code;
  return typeof m === 'function' ? m(p) : m;
}
function renderIssues() {
  const ul = $('#issues');
  ul.replaceChildren();
  const add = (msg, level) => { const li = document.createElement('li'); li.textContent = msg; if (Object.hasOwn(rank, level)) li.className = level; ul.append(li); return li; };
  // Beside a report of characters the font lacks: ONE family that has them, in one tap, with its size (ruling R14) — by
  // the script of the missing characters, and only while the font in use is a bundled one (one offer, no chain: after
  // the download the line names what is still missing, and the list stays for a choice by hand). Nothing is asked of
  // any host before the tap.
  const offer = (li, chars) => {
    const entry = fontList && offerFor(chars, fontList, doc.font, pageLanguage().id);   // ship 3: a ja / zh page offers its own family first
    if (!entry && listMayHelp(chars, doc.font)) { const n = document.createElement('span'); n.className = 'ui-offer-note'; n.textContent = S.pickFromList; li.append(n); }   // R83
    if (!entry) return;
    const b = document.createElement('button'); b.type = 'button'; b.className = 'ui-offer'; b.dataset.font = entry.id;
    b.textContent = S.useFont(entry.family, megabytes(entry.bytes));
    b.addEventListener('click', () => change({ ...doc, font: recordOf(entry) }));
    li.append(b);
  };
  if (fatal) { add(S.fatal, 'error'); return; }
  if (buildTimedOut) add(S.buildTimeout, 'error');
  if (buildFailed) add(S.buildFail, 'error');
  for (const p of [...problems].sort((a, b) => rank[a.level] - rank[b.level])) { const li = add(problemText(p), p.level); if (p.code === 'missing-chars') offer(li, p.chars); }
}
// The upload row: the button, the thumbnail of the art in use, what the reader skipped, and one line of status.
function renderUpload() {
  $('#pick').disabled = reading;
  $('#thumb').classList.toggle('hidden', !art);
  const ul = $('#skipped');
  ul.replaceChildren();
  for (const k of SKIPPED_KEYS) {
    const n = shownSkipped ? shownSkipped[k] : 0;
    if (!Number.isInteger(n) || n <= 0) continue;
    const li = document.createElement('li'); li.dataset.k = k; li.textContent = S.skipped[k](n); ul.append(li);
  }
  const note = $('#upload-note'), refused = !reading && uploadError !== null;
  note.classList.toggle('error', refused);
  note.textContent = reading ? S.reading : refused ? (art ? `${uploadError} ${S.keptOld}` : uploadError)
    : art ? S.artKept : isLineBrowser(navigator.userAgent) ? S.needFileLine : S.needFile;
}
// The art's rings, drawn from the numbers (never from the file): art is y up and centred on its box, a canvas is y down.
function drawThumb() {
  const c = $('#thumb'), dpr = Math.min(window.devicePixelRatio || 1, 2);
  c.width = THUMB[0] * dpr; c.height = THUMB[1] * dpr;             // the CSS box (main.css) times the screen's density
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, c.width, c.height);
  if (!art || !(art.w > 0 && art.h > 0)) return;
  const pad = 6 * dpr, k0 = Math.min((c.width - 2 * pad) / art.w, (c.height - 2 * pad) / art.h), cx = c.width / 2, cy = c.height / 2;
  ctx.beginPath();
  let k = 0;
  for (const n of art.rings) {
    for (let j = 0; j < n; j++, k += 2) {
      const x = cx + k0 * art.points[k], y = cy - k0 * art.points[k + 1];
      if (j === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
  }
  ctx.fillStyle = getComputedStyle(c).color;      // the page's text colour
  ctx.fill('evenodd');
}
// Bring a message into the part of the viewport the fixed bar does not cover (scroll-margin in main.css keeps the bar's height free).
function reveal(el) {
  if (!shown(el)) return;
  const r = el.getBoundingClientRect();
  if (r.top < 0 || r.bottom > $('.bar').getBoundingClientRect().top) el.scrollIntoView({ block: 'nearest' });
}
const hasError = () => problems.some((p) => p.level === 'error');
const lightBtns = (btns, k) => { for (const b of btns) { b.classList.toggle('on', b.dataset.k === k); b.setAttribute('aria-pressed', String(b.dataset.k === k)); } };
function render() {
  const logo = doc.style === 'logo', svg = logo && doc.logo.src === 'svg';
  text('#title', S.titles[doc.style]);
  $('#beta').classList.toggle('veiled', logo); if (logo) $('#beta').setAttribute('aria-hidden', 'true'); else $('#beta').removeAttribute('aria-hidden');
                       // the trial mark belongs to the two-sided style (logo spec D15 / R17); veiled, not displayed: its room keeps the header (and the style row under it) where it was
  lightBtns($('#style').children, doc.style);
  $('#two-rows').classList.toggle('hidden', logo); $('#logo-rows').classList.toggle('hidden', !logo); $('#logo-look').classList.toggle('hidden', !logo);
  // The seat pills keep their room in the logo style (veiled, not displayed), so the style row does not move under the finger;
  // with no preview they are not on the page at all.
  $('#view').classList.toggle('hidden', previewFailed); $('#view').classList.toggle('veiled', logo && !previewFailed);
  if (logo) $('#view').setAttribute('aria-hidden', 'true'); else $('#view').removeAttribute('aria-hidden');
  $('#view').toggleAttribute('inert', logo);
  lightBtns($('#src').children, doc.logo.src);
  for (const q of ['#text-row', '#text-hint']) $(q).classList.toggle('hidden', svg);
  $('#font-row').classList.toggle('hidden', svg); $('#upload-row').classList.toggle('hidden', !svg);
  lightBtns($('#finish').children, doc.logo.finish);
  // one nozzle cannot print a flush logo: the pill stays readable (aria-disabled, not `disabled`) and its note says why
  if (doc.mode === 'swap') $('#finish button[data-k="flush"]').setAttribute('aria-disabled', 'true');
  else $('#finish button[data-k="flush"]').removeAttribute('aria-disabled');
  text('#finish-note', (doc.logo.finish === 'flush' ? S.finishNote.flush : S.finishNote[doc.logo.finish](GEOM.inlay)) + (doc.mode === 'swap' ? ' ' + S.flushNeedsMulti : ''));
  if (document.activeElement !== $('#scale')) $('#scale').value = String(doc.logo.scale);
  text('#o-scale', S.percent(doc.logo.scale));
  if (document.activeElement !== $('#text')) $('#text').value = doc.logo.text;
  const noMsgColour = logo && doc.logo.finish === 'engraved';      // an engraved logo has no colour of its own: its row is not there
  colours.set([{ key: 'body', label: S.colour.body, value: doc.colors.body }, { key: 'message', label: logo ? S.logoColour : S.colour.message, value: doc.colors.message, off: noMsgColour ? 'hidden' : undefined }], LOOK_LIST);
  $('#link-notice').textContent = notice || ''; $('#link-notice').classList.toggle('hidden', !notice);
  for (const b of $('#shape').children) b.classList.toggle('on', b.dataset.k === doc.shape);
  for (const b of $('#edge').children) b.classList.toggle('on', b.dataset.k === doc.edge);
  for (const b of $('#mode').children) b.classList.toggle('on', b.dataset.k === doc.mode);
  fonts.setFont();
  // a field being typed in is left alone: its text is the person's, the doc holds the cleaned form
  for (const w of ['a', 'b']) if (document.activeElement !== $('#' + w)) $('#' + w).value = doc[w];
  if (document.activeElement !== $('#d')) $('#d').value = String(doc.d);
  text('#l-d', S.d[doc.shape]); $('#d').setAttribute('aria-label', S.d[doc.shape]);
  text('#o-d', S.mm(doc.d));
  text('#edge-note', logo ? (doc.edge === 'lip' ? S.logoEdgeNote.lip(GEOM.lip) : S.logoEdgeNote.flat) : S.edgeNote[doc.edge]); text('#mode-note', modeNoteText());
  renderReadout(); renderLegend(); renderZTable(); renderIssues(); renderUpload();
  const ok = !fatal && builtDoc === doc && builtArt === art && !buildFailed && !hasError();
  // a flush logo fills its own cut: its STL would be a plain coaster, so the STL button is off there (logo spec D17)
  const noStl = logo && doc.logo.finish === 'flush' && !!(meta && meta.logo);
  $('#dl3mf').disabled = !ok; $('#dlstl').disabled = !ok || noStl; $('#share').disabled = false;
}

// ---- change: every control goes through here. A notice about the link goes away with the first edit, unless the
// edit is the page's own correction (`keepNotice`). ----
function change(next, { keepNotice = false } = {}) {
  doc = sanitize(next);
  if (!keepNotice) notice = null;
  render(); scheduleBuild();
  window.history.replaceState(null, '', shareUrl(location.href, doc));
}

// ---- worker build loop: 120 ms debounce, latest wins (a superseded build is answered `stale`) ----
let buildTimer = null;
function scheduleBuild() { clearTimeout(buildTimer); buildTimer = setTimeout(build, 120); }
// The art a request reads: only the logo style with the `svg` source does. Nothing else is sent it, or clocked.
const artFor = (d) => (d.style === 'logo' && d.logo.src === 'svg' ? art : null);
// The uploaded file is dropped: it kept the engine past the page's patience, or broke it twice. The source stays
// `svg`; the upload row says why, in view; the page builds on without it.
function dropArt(message) {
  art = null; artSkipped = shownSkipped = null; uploadError = message;
  render(); reveal($('#upload-row')); scheduleBuild();
}
// A request that reads uploaded art has a clock (logo spec D10): no count of points bounds one boolean in the engine,
// and the engine cannot be interrupted. callOnce's clock runs from the moment the worker STARTS the request (its
// `started` message), so a request never pays for the one in front of it; the limit is read here, when it is sent —
// a build's, or an export's. A clock that fires, or a second engine fault, is the doing of the art THAT REQUEST carried:
// the worker is replaced and only that art is dropped.
async function callWorker(msg, onProgress) {
  const limit = () => window.__COASTER__[msg.type === 'export' ? 'artExportTimeoutMs' : 'artBuildTimeoutMs'];
  // A build without a file has a clock too (unify-ui ruling R22): 10 s on the worker's own work, from its `started` —
  // never while it waits behind another request or for a font file. Its timeout has a name of its own: nothing is dropped.
  const plainBuild = msg.type === 'build' && !msg.art;
  const ask = () => (msg.art ? worker.callOnce(msg, { limitMs: limit(), onProgress }) : plainBuild ? worker.callOnce(msg, { limitMs: window.__COASTER__.buildTimeoutMs, onProgress }) : worker.call(msg));
  const code = (r) => (r.error ? r.error.code : null);
  const gone = { ok: false, error: { code: 'timeout' } };       // one name for "this request's file is no longer in use": build() and download() return on it
  let r, faulted = false, reasked = false;
  for (;;) {
    r = await ask();
    // one engine fault means the wasm engine in that worker is dead: a fresh worker, and the same request once more
    if (code(r) === 'engine' && !faulted) { faulted = true; worker.replace(); continue; }
    // an export the worker lost to a swap is asked again, once — unless its art has been dropped or replaced meanwhile
    if (code(r) === 'replaced' && msg.type === 'export' && !reasked && (!msg.art || msg.art === art)) { reasked = true; continue; }
    break;
  }
  // the engine cannot be interrupted: the worker is ended and a new one started; the page says so (build(), below)
  if (plainBuild && code(r) === 'timeout') { worker.replace(); return { ok: false, error: { code: 'build-timeout' } }; }
  if (!msg.art) return r;
  if (code(r) === 'timeout' || code(r) === 'engine') {
    // the clock fired, or the engine faulted twice. Only THAT art is dropped — a newer file picked meanwhile stays, and the page builds on with it.
    worker.replace();
    if (msg.art === art) dropArt(S.svgTooBig); else scheduleBuild();
    return gone;
  }
  if (code(r) === 'replaced' && msg.type === 'export' && msg.art !== art) return gone;   // its art went while it waited: nothing to say twice
  return r;
}
let shownCodes = [];
const budgetKnob = () => (typeof window.__COASTER__.textBudget === 'number' ? { budget: window.__COASTER__.textBudget } : {});
async function build() {
  text('#busy', S.updating); $('#busy').classList.remove('hidden');
  const sent = doc, sentArt = art;
  // A downloaded family's file on its way: the line says which and how far (the worker posts progress per piece).
  const sentFont = sent.font, family = (fontList && curatedEntry(sentFont, fontList) || {}).family || sentFont.id;   // a Google family's id IS its name
  let progressSeen = false;
  const onProgress = (p) => {
    window.__COASTER__.fontProgress = { id: sentFont.id, received: p.received, total: p.total };
    if (doc === sent) text('#busy', S.loadingFont(family, p.received / 1048576, p.total / 1048576));
    // the line is in the preview and the font list is below it: it is brought into view once, when it first shows (unify-ui ruling R65)
    if (doc === sent && !progressSeen) { progressSeen = true; reveal($('#busy')); }
  };
  const reply = await callWorker({ type: 'build', doc: sent, art: artFor(sent), ...budgetKnob() }, onProgress);
  const code = reply.error && reply.error.code;
  if (code === 'stale') return;                                  // a newer build is already on its way
  if (code === 'timeout') return;                                // this request's file is gone: the next build is already scheduled
  if (code === 'replaced') { scheduleBuild(); return; }          // the worker was swapped under this request
  // A reply for a doc of the other style is dropped: its meta, problems and scene belong to a style that is no longer lit
  // (the pill's tap cleared them; scheduleBuild() has the lit style's build on its way).
  if (sent.style !== doc.style) return;
  if (doc === sent) $('#busy').classList.add('hidden');   // an older build's reply leaves the line to the build on its way (R106): its reply takes it down
  $('#info').style.minHeight = '';
  // The build's clock fired: nothing was built from this document (the downloads stay off); the next edit goes to the new worker.
  if (code === 'build-timeout') {
    buildTimedOut = true; render();
    if (!shownCodes.includes('build-timeout')) reveal($('#issues'));
    shownCodes = ['build-timeout'];
    return;
  }
  buildTimedOut = false;
  // A downloaded family that could not be had (unify-ui acceptance 11): the page stays usable. The design goes back to the
  // font it last built in (Kanit when there is none) and a notice names the family — unless the visitor has already
  // picked another font, which then simply builds.
  if (code === 'font-load' && sentFont.src === 'google') {
    notice = S.fontFailed(family);
    fonts.markFailed(fontKey(sentFont));                          // its row in the sheet says so at the next open (as name-tag's)
    if (doc.font === sentFont) change({ ...doc, font: { ...(lastFont || DEFAULT_DOC.font) } }, { keepNotice: true });
    else render();
    reveal($('#link-notice'));
    return;
  }
  // A family neither list holds (ruling R100): the worker built the design in Kanit and says so — the notice the page
  // showed for it before, and the document to Kanit (no undo step) — unless the visitor has picked another font since.
  if (reply.ok && reply.fellBack && doc.font === sentFont) { notOffered(sentFont); return; }
  builtDoc = sent; builtArt = sentArt; buildFailed = reply.ok === false;
  const firstBuild = !window.__COASTER__.built;
  if (buildFailed) problems = [];
  else {
    lastFont = sentFont;
    problems = reply.problems || []; meta = reply.meta;
    window.__COASTER__.problems = problems; window.__COASTER__.lastMs = reply.ms; window.__COASTER__.budgetInForce = reply.budget ?? null;
    const first = !builtOnce; builtOnce = true;
    if (preview) {
      preview.setScene(reply.parts);
      // The first successful build only, before `built` flips: the camera swings A -> B -> A once, unless motion is unwanted.
      if (first && sent.style === 'two' && !matchMedia('(prefers-reduced-motion: reduce)').matches) preview.swing();
    }
    window.__COASTER__.built = true; window.__COASTER__.builds++;
  }
  render();
  // A problem that was not on show a moment ago is brought into view; one that stays does not pull the page.
  const codes = [...problems.map((p) => p.code), ...(buildFailed ? ['build-failed'] : [])];
  if (codes.some((c) => !shownCodes.includes(c))) reveal($('#issues'));
  shownCodes = codes;
  // a link's notice was brought into view when the font list arrived, before this first build put its lines above it: once more now, so that it stops above the bar (unify-ui ruling R65)
  if (firstBuild && notice) reveal($('#link-notice'));
  worker.countGeneration(); worker.recycleIfDue();
}

// ---- style, logo text, finish, size ----
$('#style').addEventListener('click', (e) => {
  const b = e.target.closest('button[data-k]');
  if (!b || !STYLES.includes(b.dataset.k) || b.dataset.k === doc.style) return;
  meta = null; problems = []; window.__COASTER__.problems = problems;    // the other style's numbers are not this one's
  // the readout block keeps the height it had until the other style's build lands, so nothing below it moves under the finger
  $('#info').style.minHeight = $('#info').getBoundingClientRect().height + 'px';
  change({ ...doc, style: b.dataset.k });
  if (preview) preview.setView(b.dataset.k === 'logo' ? 'front' : 'a');
});
const setLogo = (patch) => change({ ...doc, logo: { ...doc.logo, ...patch } });
$('#text').addEventListener('input', () => setLogo({ text: $('#text').value }));
$('#text').addEventListener('blur', () => { $('#text').value = doc.logo.text; });
$('#finish').addEventListener('click', (e) => {
  const b = e.target.closest('button[data-k]');
  if (b && b.getAttribute('aria-disabled') !== 'true' && FINISHES.includes(b.dataset.k) && b.dataset.k !== doc.logo.finish) setLogo({ finish: b.dataset.k });
});
$('#scale').addEventListener('input', () => setLogo({ scale: Number($('#scale').value) }));
$('#src').addEventListener('click', (e) => {
  const b = e.target.closest('button[data-k]');
  if (b && SOURCES.includes(b.dataset.k) && b.dataset.k !== doc.logo.src) setLogo({ src: b.dataset.k });
});

// ---- uploaded logo (logo spec D9, D10). The file's text is a local of readFile() and is handed to the worker, which hands it
// to readSvg: it is never put in the DOM, a URL, storage, a log or the doc. The file's name is read once, for its
// extension, and the input lets go of the file as soon as it is picked. ----
// The limit is for the engine's work on the file. callOnce() waits for the worker that is current now, posts the file to it,
// and starts the clock when that worker says it has STARTED the request: the wait for wasm and fonts, or for a build in
// front of it, is not the file's doing. Replies of its own: `replaced` — that worker was swapped; `timeout`; `fatal`.
const askArt = (svg) => worker.callOnce({ type: 'art', svg }, { limitMs: window.__COASTER__.artTimeoutMs });
// → { art, skipped } or { error: message, skipped? }.
async function readFile(file) {
  if (!/\.svg$/i.test(file.name) && file.type !== 'image/svg+xml') return { error: S.notSvg };   // a phone's picker may hand over a real SVG with no extension, but a type
  if (file.size > SVG_MAX_BYTES) return { error: S.svgTooBig };
  let svg;
  try { svg = await file.text(); } catch { return { error: S.readFail }; }
  let r = await askArt(svg);
  if (r.error && r.error.code === 'replaced') r = await askArt(svg);   // a build's engine fault swapped the worker under this call: start over, once — the new worker, a new clock
  const code = r.ok ? null : r.error && r.error.code;
  if (code === 'engine' || code === 'timeout') {
    // This file broke the engine, or kept it past the limit. A fresh worker; the file is not sent again; the page builds as before.
    worker.replace(); scheduleBuild();
    return { error: S.svgTooBig };
  }
  // The reader's three verdicts name the file's fault. Anything else (`internal`, a second `replaced`) says nothing
  // about the file, which may be a good one: the message asks to try again and does not call it a bad SVG.
  if (code === 'fatal') return { error: S.fatal };                // the worker cannot work: no file can be read on this page
  const verdict = { 'svg-too-big': 'svgTooBig', 'svg-empty': 'svgEmpty', 'svg-bad': 'svgBad' };
  if (!r.ok) return { error: S[Object.hasOwn(verdict, code) ? verdict[code] : 'readFail'], skipped: r.skipped };
  return { art: r.art, skipped: r.skipped };
}
async function pickFile(file) {
  reading = true; render();
  let r;
  try { r = await readFile(file); } catch { r = { error: S.readFail }; }      // whatever happens, the button comes back
  reading = false;
  if (r.art) { if (notice === S.linkNeedsLogo) notice = null; art = r.art; artSkipped = shownSkipped = r.skipped || null; uploadError = null; scheduleBuild(); }
  else { uploadError = r.error; shownSkipped = r.skipped || artSkipped; }      // a refused file changes nothing but the message: the earlier art stays
  window.__COASTER__.picks++;
  render(); reveal($('#upload-row'));
  if (r.art) drawThumb();
}
$('#pick').addEventListener('click', () => $('#file').click());
$('#file').addEventListener('change', () => {
  const file = $('#file').files[0];
  $('#file').value = '';             // the input forgets the file (and its name); the same file can be picked again
  if (file && !reading) pickFile(file);
});

// ---- words, shape, size, edge ----
for (const w of ['a', 'b']) {
  $('#' + w).addEventListener('input', () => change({ ...doc, [w]: $('#' + w).value }));
  $('#' + w).addEventListener('blur', () => { $('#' + w).value = doc[w]; });
}
function segPick(sel, list, key) {
  $(sel).addEventListener('click', (e) => {
    const b = e.target.closest('button[data-k]');
    if (b && list.includes(b.dataset.k)) change({ ...doc, [key]: b.dataset.k });
  });
}
segPick('#shape', SHAPES, 'shape'); segPick('#edge', EDGES, 'edge'); segPick('#mode', MODES, 'mode');
$('#d').min = String(LIMITS.d[0]); $('#d').max = String(LIMITS.d[1]);
$('#d').addEventListener('input', () => change({ ...doc, d: Number($('#d').value) }));

// The font control (unify-ui ship 4a — @3dp/ui/font-control): the button, the weight select, the line for a family not yet
// print-tested (I4), and the sheet behind the button — the 29 recommended families, then the whole Google catalog (R91).
// fonts/curated.json is the file the worker reads too. A record that IS an entry of the list is held as the entry's own
// (R95, asListed). A design whose font this page cannot build in opens in Kanit: silently for an unknown bundled id, as
// always; with a notice that names what was asked for — an uploaded font at once, a family neither list holds when the
// worker says it built in Kanit instead (its reply's `fellBack`, ruling R100). Only the fetch is allowed to fail quietly (the
// button then stays off, Kanit the font the worker always has).
const fonts = createFontControl({ root: $('#font-pick'), S, core: PICKER_CORE, getFont: () => doc.font, getText: () => (doc.style === 'logo' ? doc.logo.text : [doc.a, doc.b].filter(Boolean).join(' ')),   // the text the coaster prints in the style in force (scrutiny B2)
  onPick: (f) => ((font) => change({ ...doc, font }))(asListed(recordOf(f), fontList)),
  onWeight: (font) => change({ ...doc, font }) });
function notOffered(asked) {
  change({ ...doc, font: { ...DEFAULT_DOC.font } }, { keepNotice: true });
  if (asked.src !== 'bundled') { notice = [notice, S.fontNotOffered(askedFor(asked))].filter(Boolean).join(' '); render(); reveal($('#link-notice')); }   // an unknown bundled id: silently, as always
}
fetch(asset('fonts/curated.json')).then((r) => r.json()).catch(() => null).then((list) => {
  if (!Array.isArray(list)) return;
  fontList = list;
  fonts.setList(list);
  const asked = doc.font;
  if (asked.src === 'google' || curatedEntry(asked, fontList)) {
    const held = asListed(asked, fontList);
    if (fontKey(held) !== fontKey(asked)) ((font) => change({ ...doc, font }))(held); else render();   // a list entry, named by another record
    return;
  }
  notOffered(asked);
});

// ---- downloads (anchor attached before click(): a detached <a download> is ignored by Safari/iOS) ----
async function download(format) {
  const sent = doc, sentMeta = meta;                              // the choices below describe the file that is made, not a later edit
  text('#busy', S.updating); $('#busy').classList.remove('hidden');
  const r = await callWorker({ type: 'export', doc: sent, art: artFor(sent), format, ...budgetKnob() });
  if (r.error && r.error.code === 'timeout') return;              // the file was dropped; the upload row says why, and the next build hides the line
  if (builtDoc === doc) $('#busy').classList.add('hidden');       // a build that is still on its way keeps the line
  worker.countGeneration(); worker.recycleIfDue();
  if (!r.ok) { const li = document.createElement('li'); li.textContent = S.exportFail; $('#issues').append(li); return; }
  const href = URL.createObjectURL(new Blob([r.bytes], { type: r.mime }));
  const a = document.createElement('a'); a.href = href; a.download = r.filename;
  document.body.appendChild(a); a.click(); a.remove();
  // Choices are sent once per download: they measure what actually gets printed. Never a word, never the file.
  hit('coaster', 'download', format);
  hit('coaster', 'choice', 'shape:' + sent.shape);
  hit('coaster', 'choice', 'edge:' + sent.edge);
  hit('coaster', 'choice', 'mode:' + sent.mode);
  hit('coaster', 'choice', 'look:' + lookKey(sent));
  hit('coaster', 'choice', 'style:' + sent.style);
  // the id — a slug, or a family name — of the font the worker BUILT the file in: a name out of a link that no list holds
  // is never sent (the worker built it in Kanit and said so, `fellBack`); the registry's pattern takes every catalog family
  const knownFont = builtFontId(r, sent.font, DEFAULT_DOC.font);
  if (sent.style === 'logo') {
    const has = !!(sentMeta && sentMeta.logo);    // a coaster with nothing on its face is not a logo coaster
    hit('coaster', 'choice', 'logo:' + (has ? sent.logo.src : 'none'));
    if (has) hit('coaster', 'choice', 'finish:' + sent.logo.finish);
    if (has && sent.logo.src === 'text') hit('coaster', 'choice', 'font:' + knownFont);
  } else {
    hit('coaster', 'choice', 'sides:' + sidesKey(sent));
    if (sidesKey(sent) !== 'none') hit('coaster', 'choice', 'font:' + knownFont);
  }
  setTimeout(() => URL.revokeObjectURL(href), 5000);
}
function wireDownload(btn, format) {
  btn.addEventListener('click', () => {
    if (isLineBrowser(navigator.userAgent)) {
      text('#line-hint', S.lineHint);
      $('#line-hint').classList.remove('hidden');
      location.href = externalBrowserUrl(location.href);
      return;
    }
    download(format);
  });
}
wireDownload($('#dl3mf'), '3mf');
wireDownload($('#dlstl'), 'stl');

// ---- share ----
$('#share').addEventListener('click', async () => {
  const sent = doc, u = shareUrl(location.href, sent);            // forShare(): with an uploaded logo the link carries the mode, never the file or a text
  try { await navigator.clipboard.writeText(u); } catch { prompt('', u); }
  hit('coaster', 'share');
  if (sent.style === 'logo' && sent.logo.src === 'svg') { notice = S.shareNoLogo; render(); reveal($('#link-notice')); }
  text('#share', S.copied);
  setTimeout(() => { text('#share', S.share); }, 1500);
});

// ---- start ----
render();
build();
